"use client"

import { useState } from "react"
import { Card } from "#/components/ui/card"
import { Badge } from "#/components/ui/badge"
import { Play, FileText, Type, Clock } from "lucide-react"
import type { Post } from "#/lib/types"

interface MosaicGridProps {
  posts: Post[]
}

// Define different tile sizes for the mosaic effect
const getTileSize = (index: number) => {
  const patterns = [
    "col-span-2 row-span-2", // Large square
    "col-span-1 row-span-1", // Small square
    "col-span-1 row-span-1", // Small square
    "col-span-2 row-span-1", // Wide rectangle
    "col-span-1 row-span-2", // Tall rectangle
    "col-span-1 row-span-1", // Small square
    "col-span-1 row-span-1", // Small square
    "col-span-2 row-span-1", // Wide rectangle
  ]
  return patterns[index % patterns.length]
}

export function MosaicGrid({ posts }: MosaicGridProps) {
  const [selectedPost, setSelectedPost] = useState<Post | null>(null)

  const renderPostPreview = (post: Post, size: string) => {
    const baseClasses = `relative overflow-hidden rounded-lg cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:z-10 ${size}`

    switch (post.type) {
      case "photo":
        return (
          <Card className={baseClasses} onClick={() => setSelectedPost(post)}>
            <img
              src={post.imageUrl || "/placeholder.svg"}
              alt={post.imageAlt || "Post"}
              className="w-full h-full object-cover transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors duration-300" />
          </Card>
        )

      case "video":
        return (
          <Card className={baseClasses} onClick={() => setSelectedPost(post)}>
            <img
              src={post.thumbnailUrl || "/placeholder.svg"}
              alt="Video thumbnail"
              className="w-full h-full object-cover transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
              <Play className="h-8 w-8 text-white transition-transform duration-300 hover:scale-110" />
            </div>
            <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs">
              {Math.floor(post.duration / 60)}:
              {(post.duration % 60).toString().padStart(2, "0")}
            </div>
          </Card>
        )

      case "text":
        return (
          <Card
            className={`${baseClasses} p-4 flex items-center justify-center text-center transition-transform duration-300`}
            style={{
              backgroundColor: post.backgroundColor,
              color: post.textColor,
            }}
            onClick={() => setSelectedPost(post)}
          >
            <div className="space-y-2">
              <Type className="h-6 w-6 mx-auto opacity-60" />
              <p className="text-sm font-medium line-clamp-3">{post.content}</p>
            </div>
          </Card>
        )

      case "article":
        return (
          <Card className={baseClasses} onClick={() => setSelectedPost(post)}>
            {post.coverImage ? (
              <img
                src={post.coverImage || "/placeholder.svg"}
                alt="Article cover"
                className="w-full h-full object-cover transition-transform duration-300"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-muted to-muted/50 p-4 flex flex-col justify-between">
                <div>
                  <FileText className="h-6 w-6 mb-2 text-muted-foreground" />
                  <h3 className="font-semibold text-sm line-clamp-2">
                    {post.title}
                  </h3>
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  <span>{post.readTime} min</span>
                </div>
              </div>
            )}
            <div className="absolute top-2 left-2">
              <Badge variant="secondary" className="text-xs">
                Article
              </Badge>
            </div>
          </Card>
        )

      default:
        return null
    }
  }

  return (
    <div className="space-y-6">
      {/* Mosaic Grid */}
      <div className="grid grid-cols-3 md:grid-cols-4 gap-2 auto-rows-[120px] md:auto-rows-[150px]">
        {posts.map((post, index) => (
          <div
            key={post.id}
            className={`${getTileSize(index)} animate-in fade-in-0 slide-in-from-bottom-2`}
            style={{ animationDelay: `${index * 50}ms` }}
          >
            {renderPostPreview(post, "h-full")}
          </div>
        ))}
      </div>

      {/* Empty State */}
      {posts.length === 0 && (
        <div className="text-center py-12 animate-in fade-in-0 slide-in-from-bottom-4">
          <div className="space-y-3">
            <div className="h-16 w-16 mx-auto bg-muted rounded-full flex items-center justify-center">
              <FileText className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold">No posts yet</h3>
            <p className="text-muted-foreground">
              Start sharing your creativity with the world!
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
